import { FC, ReactNode, memo, useMemo } from 'react';
import classNames from 'classnames';
import Typography from '@mui/material/Typography';
import formatDistanceStrict from 'date-fns/formatDistanceStrict';
import differenceInSeconds from 'date-fns/differenceInSeconds';

import { useStyles } from './EvalStatus.styles';
import { EvaluationStatus } from '@modules/EvaluationModule/enums/EvaluationStatus';
import { EvaluationType } from '@modules/EvaluationModule/enums/EvaluationType';
import { differenceInCalendarMonths, differenceInMonths, format } from 'date-fns';
import { EopResult } from '@modules/EvaluationModule/enums/EopResult';
import { getEvaluationTerms } from '@shared/helpers/evaluationTerminology';

interface Props {
  evaluationDate: string;
  eopResolvedAt?: string | null; // If there is an end of period resolve date, we use it instead of evaluationDate
  status: string;
  typeOfInteraction: EvaluationType;
  eopStatus: EopResult | null;
  eopExtendedTo: string | null;
  officeCountry?: string;
  placeholder?: ReactNode;
}

const EvalStatusComponent: FC<Props> = ({
  evaluationDate,
  eopResolvedAt,
  status,
  typeOfInteraction,
  eopStatus,
  eopExtendedTo,
  officeCountry,
  placeholder,
}) => {
  const styles = useStyles();
  const evaluationTerms = getEvaluationTerms(officeCountry);

  const today = useMemo(() => new Date(), []);
  const isOverdue = differenceInSeconds(new Date(evaluationDate), today) < 0 && status === EvaluationStatus.Upcoming;

  const { text, style } = useMemo(() => {
    if (status === EvaluationStatus.Cancelled) return { text: 'Cancelled', style: styles.evalStatus };
    if (status === EvaluationStatus.Closed) return { text: 'Closed', style: styles.evalStatus };
    if (isOverdue) return { text: 'Pending to start', style: styles.overdue };

    const timeLeftText = formatDistanceStrict(new Date(evaluationDate), today, {
      roundingMethod: 'round',
    });

    const isOngoing = status === EvaluationStatus.Ongoing;
    const isEOP = typeOfInteraction === EvaluationType.EndOfProbation;
    const diffInMonths = differenceInMonths(new Date(evaluationDate), today);

    if (isOngoing) {
      if (differenceInCalendarMonths(new Date(evaluationDate), today) <= -1) {
        return { text: 'Pending to close', style: styles.overdue };
      }
      return { text: 'Ongoing', style: styles.ongoing };
    }

    if (isEOP) {
      return { text: `${evaluationTerms.endOfPeriodAbbr} in ${timeLeftText}`, style: '' };
    }
    if (diffInMonths === 0) return { text: 'This month', style: '' };
    if (diffInMonths === 1) return { text: 'Next month', style: '' };
    return { text: `Upcoming evaluation in ${timeLeftText}`, style: '' };
  }, [
    evaluationDate,
    isOverdue,
    status,
    styles.evalStatus,
    styles.ongoing,
    styles.overdue,
    today,
    typeOfInteraction,
    evaluationTerms,
  ]);

  const eopDateText = useMemo(() => {
    if (eopResolvedAt && (eopStatus === EopResult.Passed || eopStatus === EopResult.NotPassed)) {
      return evaluationTerms.periodEndedOn + '\xA0' + format(new Date(eopResolvedAt), 'MMM d');
    } else if (eopExtendedTo && eopStatus === EopResult.Extended) {
      return evaluationTerms.periodExtendedTo + '\xA0' + format(new Date(eopExtendedTo), 'MMM d');
    } else {
      return evaluationTerms.periodEndsOn + '\xA0' + format(new Date(evaluationDate), 'MMM d');
    }
  }, [eopResolvedAt, evaluationDate, eopStatus, eopExtendedTo]);

  return (
    <>
      <Typography className={classNames(styles.evalStatus, style)}>{text}</Typography>
      {typeOfInteraction === EvaluationType.EndOfProbation ? (
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          {eopDateText}
        </Typography>
      ) : (
        placeholder
      )}
    </>
  );
};

export const EvalStatus = memo(EvalStatusComponent);
